import React, { useState, useRef } from 'react';
import './videocarousel.css';
import { FaSearch } from 'react-icons/fa';

function isVideoUrl(url) {
  if (!url) return false;
  return (
    url.endsWith('.mp4') ||
    url.endsWith('.mov') ||
    url.includes('/video/upload/')
  );
}
function isImageUrl(url) {
  if (!url) return false;
  return (
    url.endsWith('.jpg') ||
    url.endsWith('.jpeg') ||
    url.endsWith('.png') ||
    url.endsWith('.gif') ||
    url.endsWith('.webp') ||
    url.endsWith('.bmp') ||
    url.endsWith('.svg')
  );
}

export default function VideoCarousel({ messages }) {
  // Filter for both images and videos
  let media = messages?.filter(
    (msg) => msg.img && (isVideoUrl(msg.img) || isImageUrl(msg.img)),
  );
  // Sort by most recent first
  media = media.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showMedia, setShowMedia] = useState(false);
  const [search, setSearch] = useState('');
  const thumbnailsRef = useRef(null);
  const videoRefs = useRef([]);
  const [thumbLoaded, setThumbLoaded] = useState({});

  // Filter media by search (regex, case-insensitive, matches anywhere in text)
  const filteredMedia = search
    ? media.filter((msg) => msg.text && new RegExp(search, 'i').test(msg.text))
    : media;

  // Reset selectedIdx if filteredMedia shrinks
  React.useEffect(() => {
    if (selectedIdx >= filteredMedia.length) setSelectedIdx(0);
  }, [filteredMedia.length, selectedIdx]);

  if (!media.length) return null;

  const scrollThumbnails = (dir) => {
    if (thumbnailsRef.current) {
      const scrollAmount = thumbnailsRef.current.offsetWidth / 1.5;
      thumbnailsRef.current.scrollBy({
        left: dir === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="video-carousel-outer">
      <h3 style={{ textAlign: 'center', margin: '0 0 12px 0' }}>
        Video Carousel
      </h3>

      <div className="carousel-search-bar">
        <span className="carousel-search-icon">
          <FaSearch />
        </span>
        <input
          type="text"
          placeholder="Search media by message text..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
        />
      </div>
      <div className="video-carousel">
        <button
          aria-label="Previous media"
          className="carousel-arrow"
          onClick={() => scrollThumbnails('left')}
        >
          &#8592;
        </button>
        <div className="carousel-thumbnails" ref={thumbnailsRef}>
          {filteredMedia.length === 0 && (
            <div style={{ color: '#aaa', padding: 24 }}>No media found.</div>
          )}
          {filteredMedia.map((msg, idx) => {
            const isVideo = isVideoUrl(msg.img);
            return (
              <div
                key={msg._id}
                className={`carousel-thumb${
                  idx === selectedIdx ? ' selected' : ''
                }`}
                onClick={() => {
                  setSelectedIdx(idx);
                  setShowMedia(true);
                }}
                onMouseEnter={() => {
                  if (isVideo && videoRefs.current[idx]) {
                    videoRefs.current[idx].currentTime = 0;
                    videoRefs.current[idx].play();
                  }
                }}
                onMouseLeave={() => {
                  if (isVideo && videoRefs.current[idx]) {
                    videoRefs.current[idx].pause();
                    videoRefs.current[idx].currentTime = 0;
                  }
                }}
              >
                {!thumbLoaded[msg._id] && (
                  <div className="carousel-thumb-spinner" />
                )}
                {isVideo ? (
                  <video
                    ref={(el) => (videoRefs.current[idx] = el)}
                    src={msg.img}
                    muted
                    playsInline
                    preload="metadata"
                    className="carousel-thumb-video"
                    style={!thumbLoaded[msg._id] ? { display: 'none' } : {}}
                    onLoadedData={() =>
                      setThumbLoaded((l) => ({ ...l, [msg._id]: true }))
                    }
                  />
                ) : (
                  <img
                    src={msg.img}
                    alt="media"
                    className="carousel-thumb-video"
                    style={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '100%',
                      display: thumbLoaded[msg._id] ? 'block' : 'none',
                    }}
                    onLoad={() =>
                      setThumbLoaded((l) => ({ ...l, [msg._id]: true }))
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
        <button
          aria-label="Next media"
          className="carousel-arrow"
          onClick={() => scrollThumbnails('right')}
        >
          &#8594;
        </button>
      </div>
      {/* Modal for media playback */}
      {showMedia && filteredMedia[selectedIdx] && (
        <div className="carousel-modal" onClick={() => setShowMedia(false)}>
          <div
            className="carousel-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideoUrl(filteredMedia[selectedIdx].img) ? (
              <video
                src={filteredMedia[selectedIdx].img}
                controls
                autoPlay
                className="carousel-main-video"
              />
            ) : (
              <img
                src={filteredMedia[selectedIdx].img}
                alt="media"
                className="carousel-main-video"
                style={{ objectFit: 'contain', background: '#000' }}
              />
            )}
            <button
              className="carousel-modal-close"
              onClick={() => setShowMedia(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
